import { useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useHttpEventStream, closeHttpEventStream } from '../../utils/httpEventStreams';
import {
  getFormDataFieldsFromRequest,
  normalizeFormDataFields,
  ensureTrailingEmptyFormDataRow,
} from '../../utils/multipart';
import { urlFromParams, normalizeRequestUrlAndParams } from '../../utils/urlParams';
import { deriveRequestNameFromUrl, isUnsetRequestName } from '../../utils/deriveRequestNameFromUrl';
import { ensureTrailingEmptyKvRow } from '../../utils/kvRows';
import { normalizeAuth } from '../../utils/requestAuth';
import { normalizeEditorTab, normalizeResponseTab, normalizeMock } from '../../utils/workspaceTabPersistence';
import { normalizeRequestBodyMode, isGraphQLBodyMode } from '../../utils/requestBodyMode';
import { normalizeGraphQLBody } from '../../utils/graphqlSchema';

/** Keeps the editor in step with the tab: applying tab data, name editing, URL focus and the registrations the tab strip uses. */
export function useRequestEditorSync({
  url,
  auth,
  response,
  body,
  initialData,
  onUiStateChange,
  onUpdateResponse,
  loading,
  onRegisterRenameShortcut,
  name,
  onRegisterLiveState,
  onRequestNameCommit,
  focusUrlNonce,
  syncRevision,
  emitDataChange,
  params,
  headers,
  cookies,
  setName,
  method,
  setMethod,
  setUrl,
  setUrlImportNonce,
  setHeaders,
  setParams,
  setCookies,
  setBody,
  bodyType,
  setBodyType,
  setRawBodyLanguage,
  setGqlSchemaStatus,
  setGqlSchema,
  setGqlSchemaError,
  setGqlFetchNonce,
  gqlAutoFetchedUrlRef,
  setFormDataFields,
  setAuth,
  setPreRequestScript,
  setTestScript,
  setResponseExamples,
  setTestResults,
  setTestScriptErrors,
  setActiveTab,
  setResponseViewerTab,
  setLoadingElapsed,
  cancelTokenRef,
  onClearTabLoadingRef,
  editingRequestName,
  setEditingRequestName,
  nameInputRef,
  nameSnapshotRef,
  lastTabIdRef,
  nameRef,
  tabFieldsRef,
}) {
  /** Tracks URL for "follow auto-title when URL changes" on new requests */
  const prevUrlForAutoNameRef = useRef(initialData?.url ?? '');
  const urlInputRef = useRef(null);
  const urlRef = useRef(url);
  urlRef.current = url;

  const notifyParamsAndUrl = useCallback(
    (newParams, overrides = {}) => {
      const nextUrl = urlFromParams(urlRef.current, newParams);
      setUrl(nextUrl);
      emitDataChange({
        name,
        method,
        url: nextUrl,
        headers,
        params: newParams,
        cookies,
        body,
        bodyType,
        auth,
        ...overrides,
      });
    },
    [name, method, headers, cookies, body, bodyType, auth, emitDataChange, setUrl]
  );

  useEffect(() => {
    if (editingRequestName) {
      requestAnimationFrame(() => {
        const el = nameInputRef.current;
        if (!el) return;
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      });
    }
  }, [editingRequestName, nameInputRef]);

  useEffect(() => {
    if (!focusUrlNonce) return;
    const id = requestAnimationFrame(() => {
      const el = urlInputRef.current;
      if (!el) return;
      const active = document.activeElement;
      if (
        active &&
        active !== el &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable)
      ) {
        return;
      }
      el.focus();
      const len = (el.textContent || '').length;
      if (len > 0 && typeof window.getSelection === 'function') {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [focusUrlNonce, initialData?.id]);

  // Tick elapsed time while a request is in flight.
  useEffect(() => {
    if (!loading) {
      setLoadingElapsed(0);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      setLoadingElapsed(Math.round((Date.now() - start) / 100) / 10);
    }, 100);
    return () => clearInterval(id);
  }, [loading, setLoadingElapsed]);

  // The tab's event stream, when its last answer was one (utils/httpEventStreams.js).
  const eventStream = useHttpEventStream(initialData?.id);
  const isEventStreaming = eventStream.status === 'streaming';

  const cancelRequest = useCallback(() => {
    cancelTokenRef.current += 1;
    closeHttpEventStream(lastTabIdRef.current); // a cancelled request must not open its stream when the head arrives
    onUpdateResponse?.({ loading: false });
  }, [onUpdateResponse, cancelTokenRef, lastTabIdRef]);

  const applyInitialDataToEditor = useCallback((data) => {
    if (!data?.id) return;
    const prevId = lastTabIdRef.current;
    if (prevId && prevId !== data.id) {
      // Switching to a different tab — invalidate any in-flight request from the old tab
      // so its response callback doesn't overwrite the new tab's state.
      cancelTokenRef.current += 1;
      onClearTabLoadingRef.current?.(prevId);
    }
    lastTabIdRef.current = data.id;
    setName(data.name || 'New Request');
    setMethod(data.method || 'GET');
    const { displayUrl, paramRows: normalizedRows } = normalizeRequestUrlAndParams(
      data.url,
      data.params
    );
    const ts = Date.now();
    const paramRows = ensureTrailingEmptyKvRow(
      normalizedRows.map((p, i) => ({
        ...p,
        _id: data.params?.[i]?._id || `p-${i}-${ts}`,
      })),
      'p'
    );
    prevUrlForAutoNameRef.current = displayUrl;
    setUrl(displayUrl);
    setHeaders(
      ensureTrailingEmptyKvRow(
        data.headers?.map((h, i) => ({ ...h, _id: h._id || `h-${i}-${Date.now()}` })) ?? [],
        'h'
      )
    );
    setParams(paramRows);
    setCookies(
      ensureTrailingEmptyKvRow(
        data.cookies?.map((c, i) => ({ ...c, _id: c._id || `c-${i}-${Date.now()}` })) ?? [],
        'c'
      )
    );
    const bodyMode = normalizeRequestBodyMode(data);
    const nextBody =
      bodyMode.bodyType === 'graphql'
        ? normalizeGraphQLBody(data.body)
        : (data.body || '');
    setBody(nextBody);
    setBodyType(bodyMode.bodyType);
    setRawBodyLanguage(bodyMode.rawBodyLanguage);
    setFormDataFields(
      ensureTrailingEmptyFormDataRow(
        normalizeFormDataFields(getFormDataFieldsFromRequest(data)).map((row, i) => ({
          ...row,
          _id: row._id || `fd-${i}-${Date.now()}`,
        }))
      )
    );
    setAuth(normalizeAuth(data.auth, { forRequest: true }));
    setPreRequestScript(data.preRequestScript ?? '');
    setTestScript(data.testScript ?? '');
    setResponseExamples(normalizeMock(data.mock).responseExamples);
    setTestResults(null);
    setTestScriptErrors(null);
    setActiveTab(normalizeEditorTab(data.editorTab));
    setResponseViewerTab(normalizeResponseTab(data.responseTab));
    setEditingRequestName(false);
    gqlAutoFetchedUrlRef.current = '';
    setGqlSchema(null);
    setGqlSchemaStatus('idle');
    setGqlSchemaError(null);
    setGqlFetchNonce((n) => n + 1);
    // Force the URL contenteditable to re-sync even if its isUpdatingRef guard is
    // still set from a prior render's requestAnimationFrame — this is the same
    // escape hatch used for cURL paste (forceSyncNonce in SyntaxHighlightInput).
    setUrlImportNonce((n) => n + 1);
  }, [cancelTokenRef, gqlAutoFetchedUrlRef, lastTabIdRef, onClearTabLoadingRef, setActiveTab, setAuth, setBody, setBodyType, setCookies, setEditingRequestName, setFormDataFields, setGqlFetchNonce, setGqlSchema, setGqlSchemaError, setGqlSchemaStatus, setHeaders, setMethod, setName, setParams, setPreRequestScript, setRawBodyLanguage, setResponseExamples, setResponseViewerTab, setTestResults, setTestScript, setTestScriptErrors, setUrl, setUrlImportNonce]);

  const selectEditorTab = useCallback(
    (key) => {
      const normalized = normalizeEditorTab(key);
      setActiveTab(normalized);
      onUiStateChange?.({ editorTab: normalized });
    },
    [onUiStateChange, setActiveTab]
  );

  const selectResponseViewerTab = useCallback(
    (key) => {
      const normalized = normalizeResponseTab(key);
      setResponseViewerTab(normalized);
      onUiStateChange?.({ responseTab: normalized });
    },
    [onUiStateChange, setResponseViewerTab]
  );

  const prevSyncRevisionRef = useRef(syncRevision);

  useLayoutEffect(() => {
    if (!initialData?.id) return;

    const tabChanged = initialData.id !== lastTabIdRef.current;
    const revisionBumped = syncRevision !== prevSyncRevisionRef.current;
    if (revisionBumped) {
      prevSyncRevisionRef.current = syncRevision;
    }

    if (tabChanged || revisionBumped) {
      applyInitialDataToEditor(initialData);
      return;
    }

    const headersKey = (hs) =>
      (hs || [])
        .filter((h) => h.key || h.value)
        .map((h) => `${h.key}\x00${h.value}\x00${h.enabled}`)
        .join('\x01');
    const bodiesEqual = (left, right) => {
      const bodyMode = normalizeRequestBodyMode(initialData);
      if (isGraphQLBodyMode(bodyMode.bodyType)) {
        const normalizedLeft = normalizeGraphQLBody(left);
        const normalizedRight = normalizeGraphQLBody(right);
        return (
          normalizedLeft.query === normalizedRight.query
          && normalizedLeft.variables === normalizedRight.variables
          && normalizedLeft.operationName === normalizedRight.operationName
        );
      }
      return (left ?? '') === (right ?? '');
    };
    const fieldsChanged =
      !bodiesEqual(initialData.body, tabFieldsRef.current.body) ||
      (initialData.url ?? '') !== (tabFieldsRef.current.url ?? '') ||
      (initialData.method ?? 'GET') !== (tabFieldsRef.current.method ?? 'GET') ||
      headersKey(initialData.headers) !== headersKey(tabFieldsRef.current.headers);
    if (fieldsChanged) {
      applyInitialDataToEditor(initialData);
    }
  }, [initialData, applyInitialDataToEditor, syncRevision, lastTabIdRef, tabFieldsRef]);

  // Keep breadcrumb request name in sync when sidebar rename updates the active tab.
  useEffect(() => {
    if (!initialData?.id || initialData.id !== lastTabIdRef.current) {
      return;
    }
    if (editingRequestName) {
      return;
    }
    const nextName = initialData.name || 'New Request';
    if (nextName !== nameRef.current) {
      setName(nextName);
    }
  }, [initialData?.name, initialData?.id, editingRequestName, lastTabIdRef, nameRef, setName]);

  // New requests: if the user never set a title, keep it in sync with the URL.
  useEffect(() => {
    if (!initialData?.isNewRequest) return;

    const trimmedUrl = (url || '').trim();
    const prevUrl = prevUrlForAutoNameRef.current;
    const prevDerived = deriveRequestNameFromUrl(prevUrl);
    const nextDerived = deriveRequestNameFromUrl(trimmedUrl);

    const n = (nameRef.current || '').trim();
    const defaultOrUnset = isUnsetRequestName(nameRef.current);
    const nameMatchedPreviousDerived = prevDerived !== '' && n === prevDerived;

    prevUrlForAutoNameRef.current = trimmedUrl;

    if (!(defaultOrUnset || nameMatchedPreviousDerived)) return;
    if (!nextDerived || nextDerived === n) return;

    setName(nextDerived);
    emitDataChange({
      ...tabFieldsRef.current,
      name: nextDerived,
    });
  }, [url, initialData?.isNewRequest, emitDataChange, nameRef, setName, tabFieldsRef]);

  useEffect(() => {
    if (response) return;
    setResponseViewerTab('body');
    onUiStateChange?.({ responseTab: 'body' });
  }, [response, onUiStateChange, setResponseViewerTab]);

  const startRequestNameEdit = useCallback(() => {
    nameSnapshotRef.current = name;
    setEditingRequestName(true);
  }, [name, nameSnapshotRef, setEditingRequestName]);

  const commitRequestNameEdit = useCallback(() => {
    const trimmed = (name || '').trim();
    const snapshot = nameSnapshotRef.current ?? name;

    if (!trimmed) {
      const prev = snapshot || initialData?.name || 'New Request';
      setName(prev);
      emitDataChange({ name: prev, method, url, headers, params, cookies, body, bodyType, auth });
      setEditingRequestName(false);
      return;
    }

    if (trimmed !== name) {
      setName(trimmed);
      emitDataChange({ name: trimmed, method, url, headers, params, cookies, body, bodyType, auth });
    }
    setEditingRequestName(false);

    if (trimmed !== (snapshot || '').trim()) {
      onRequestNameCommit?.(trimmed, (snapshot || '').trim());
    }
  }, [name, initialData?.name, method, url, headers, params, cookies, body, bodyType, auth, emitDataChange, onRequestNameCommit, nameSnapshotRef, setEditingRequestName, setName]);

  useEffect(() => {
    onRegisterRenameShortcut?.(startRequestNameEdit);
    return () => onRegisterRenameShortcut?.(null);
  }, [onRegisterRenameShortcut, startRequestNameEdit]);

  useEffect(() => {
    if (!onRegisterLiveState) return undefined;
    onRegisterLiveState(() => ({ ...tabFieldsRef.current }));
    return () => onRegisterLiveState(null);
  }, [onRegisterLiveState, tabFieldsRef]);

  return {
    urlInputRef,
    notifyParamsAndUrl,
    eventStream,
    isEventStreaming,
    cancelRequest,
    selectEditorTab,
    selectResponseViewerTab,
    commitRequestNameEdit,
  };
}
